import {IEntryCategory} from "../types/Category";
import {EntryType, IEntry} from "../types/Entry";
import {IHomepageFeaturedEntriesRes} from "../types/HomepageFeaturedEntry";
import {newshubApi} from "./newshub_api";

export interface IEntryRes {
    results: Array<IEntry>;
    count: number;
    page_size: number;
}

export interface IPressInformationQuery {
    page: number;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
}

export interface IPressReviewQuery {
    page: number;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
}

export interface IEntryVendorQuery {
    category: number;
    page: number;
    vendorId?: number;
    regionId?: ProvincialCity;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
}

export interface IEntryTestQuery {
    category: Array<IEntryCategory>;
    page: number;
}

export interface IArticlesByCategoryIdsQuery {
    categories: number[];
    page?: number;
    pageSize?: number;
}

export enum ProvincialCity {
    WROCLAW = "T02",
    BYDGOSZCZ = "T04",
    LUBLIN = "T06",
    GORZOW_WIELKOPOLSKI = "T08",
    LODZ = "T10",
    KRAKOW = "T12",
    WARSZAWA = "T14",
    OPOLE = "T16",
    RZESZOW = "T18",
    BIALYSTOK = "T20",
    GDANSK = "T22",
    KATOWICE = "T24",
    KIELCE = "T26",
    OLSZTYN = "T28",
    POZNAN = "T30",
    SZCZECIN = "T32"
}

export const getEntriesApi = newshubApi.injectEndpoints({
    endpoints: (build) => ({
        getHighlightedEntries: build.query<IEntryRes, void>({
            query: () => ({
                url: `/entries/?is_highlighted=true&type=${EntryType.BIG_DATA_NEWS}`,
                method: "GET"
            })
        }),
        getHomepageFeaturedEntries: build.query<IHomepageFeaturedEntriesRes, void>({
            query: () => ({
                url: `/homepage-featured-entries/?page_size=100`,
                method: "GET"
            })
        }),
        getNewsBarEntries: build.query<IEntryRes, void>({
            query: () => ({
                url: `/entries/?show_in_news_bar=true`,
                method: "GET"
            })
        }),
        getEntriesById: build.query<IEntry, string>({
            query: (id) => ({
                url: `/entries/${id}`,
                method: "GET"
            })
        }),
        getEntriesByType: build.query<IEntryRes, number>({
            query: (type) => ({
                url: `/entries/?type=${type}&page_size=10`,
                method: "GET"
            })
        }),
        getArticles: build.query<IEntryRes, number>({
            query: (page) => ({
                url: `/entries/?type=${EntryType.ARTICLE}&page_size=10&page=${page}`,
                method: "GET"
            })
        }),
        getBigDataNews: build.query<IEntryRes, number>({
            query: (page) => ({
                url: `/entries/?type=${EntryType.BIG_DATA_NEWS}&page_size=5&page=${page}`,
                method: "GET"
            })
        }),
        getPressInformation: build.query<IEntryRes, IPressInformationQuery>({
            query: ({page = 1, dateFrom, dateTo, search}) => ({
                url: `/entries/?type=${EntryType.PRESS_INFORMATION}&page_size=10&page=${page}${dateFrom ? `&date_from=${dateFrom}` : ""}${
                    dateTo ? `&date_to=${dateTo}` : ""
                }${search ? `&search=${search}` : ""}`,
                method: "GET"
            })
        }),
        getPressReview: build.query<IEntryRes, IPressReviewQuery>({
            query: ({page = 1, dateFrom, dateTo, search}) => ({
                url: `/entries/?type=${EntryType.PRESS_REVIEW}&page_size=10&page=${page}${dateFrom ? `&date_from=${dateFrom}` : ""}${
                    dateTo ? `&date_to=${dateTo}` : ""
                }${search ? `&search=${search}` : ""}`,
                method: "GET"
            })
        }),
        getArticlesByCategory: build.query<IEntryRes, IEntryVendorQuery>({
            query: ({category, page = 1, vendorId, regionId, dateFrom, dateTo, search}) => ({
                url: `/entries/?type=${EntryType.ARTICLE}${
                    category > -1 ? `&category=${category}` : ""
                }&page=${page}&page_size=10${
                    (category === 1 || category === 2) && vendorId ? `&vendor=${vendorId}` : ""
                }${regionId ? `&match_id=${regionId}` : ""}${dateFrom ? `&date_from=${dateFrom}` : ""}${
                    dateTo ? `&date_to=${dateTo}` : ""
                }${search ? `&search=${search}` : ""}`,
                method: "GET"
            })
        }),
        getHighlightedArticlesByCategory: build.query<IEntryRes, {category: number}>({
            query: ({category}) => ({
                url: `/entries/?type=${EntryType.ARTICLE}&page_size=10?&is_highlighted=true${
                    category > -1 ? `&category=${category}` : ""
                }`,
                method: "GET"
            })
        }),
        getArticlesByCategories: build.query<IEntryRes, IEntryTestQuery>({
            query: ({category, page = 1}) => ({
                url: category
                    ? `/entries/?type=${EntryType.ARTICLE}&${category
                          .map((category) => `category=${category.id}`)
                          .join("&")}&page=${page}&page_size=10`
                    : `/entries/?type=${EntryType.ARTICLE}&page=${page}&page_size=10`,
                method: "GET"
            })
        }),
        getArticlesByCategoryIds: build.query<IEntryRes, IArticlesByCategoryIdsQuery>({
            query: ({categories, page = 1, pageSize = 10}) => {
                const categoriesQuery = categories.map((id) => `category=${id}`).join("&");

                return {
                    url: `/entries/?type=${EntryType.ARTICLE}${categoriesQuery ? `&${categoriesQuery}` : ""}&page=${page}&page_size=${pageSize}`,
                    method: "GET"
                };
            }
        }),
        getArticlesByAuthor: build.query<IEntryRes, {authorId: string; page?: number}>({
            query: ({authorId, page = 1}) => ({
                url: `/entries/?type=${EntryType.ARTICLE}&author=${authorId}&page=${page}&page_size=5`,
                method: "GET"
            })
        })
    })
});

export const {
    useGetHighlightedArticlesByCategoryQuery,
    useGetHomepageFeaturedEntriesQuery,
    useGetHighlightedEntriesQuery,
    useGetNewsBarEntriesQuery,
    useGetEntriesByIdQuery,
    useGetEntriesByTypeQuery,
    useGetBigDataNewsQuery,
    useGetPressInformationQuery,
    useGetPressReviewQuery,
    useGetArticlesByCategoryQuery,
    useGetArticlesByCategoriesQuery,
    useGetArticlesByCategoryIdsQuery,
    useGetArticlesByAuthorQuery,
    useGetArticlesQuery
} = getEntriesApi;
